import React from 'react';
import SiteSelector from '../SiteSelector';
import D3HybridViewer from '../graph-viz/D3HybridViewer';
import DetailsPanel from '../graph-viz/DetailsPanel';
import { useNetworkViewer } from '../../hooks/useNetworkViewer';
import { Layers } from 'lucide-react';

const NetworkViewerTab: React.FC = () => {
  const {
    currentSite,
    graphData,
    visibleNodeIds,
    selectedNodes,
    selectedNodeIds,
    orientation,
    expandedIds,
    powerVisMode,
    highlightedPath,
    isSidebarOpen,
    nodeClickPhase,
    shouldSwitchToDetails,
    isLoading,
    isProcessing,
    isExpansionReady,
    expandingNodeId,
    setOrientation,
    setPowerVisMode,
    setIsSidebarOpen,
    handleNodeClick,
    handleToggleExpand,
    handleBackgroundClick,
    handleMultiNodeSelect,
    handleSearchNodeSelect,
    handleLegendClick,
    handleToggleType,
    handleCollapseTypeAndBelow,
    handleExpandAuxiliary,
    handleCollapseAuxiliary,
    handlePowerSelect,
    handlePropertySelect,
    handleDetailsPanelClose,
  } = useNetworkViewer();

  // Show loading spinner while fetching data, processing, or waiting for expansion initialization
  if (isLoading || isProcessing || (graphData.nodes.length > 0 && !isExpansionReady)) {
    const message = isProcessing ? 'Processing Graph...' : !isExpansionReady ? 'Initializing View...' : 'Loading Graph Data...';
    return (
      <div className="w-full h-full flex items-center justify-center bg-[var(--color-zone-recessed)] eg-text-primary">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <div>{message}</div>
        </div>
      </div>
    );
  }

  // Show "No Site Selected"
  if (!currentSite) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[var(--color-zone-recessed)] eg-text-secondary p-6">
        <div className="flex flex-col items-center mb-6">
          <Layers size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Site Selected</div>
          <p className="mt-2 text-sm opacity-75 mb-4">Please select a site to view the network.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  // Show empty state
  if (graphData.nodes.length === 0 && !isLoading && !isProcessing) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center bg-[var(--color-zone-recessed)] eg-text-secondary p-6">
        <div className="flex flex-col items-center mb-6">
          <Layers size={48} className="mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">No Components Found</div>
          <p className="mt-2 text-sm opacity-75 mb-4">This site has no components to display.</p>
        </div>
        <div className="w-full max-w-md">
          <SiteSelector />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[var(--color-zone-recessed)] relative overflow-hidden flex flex-col">
      {/* Site Selector - Top Bar */}
      <div className="p-4" style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-sm)' }}>
        <div className="max-w-md">
          <SiteSelector />
        </div>
      </div>

      {/* Graph Viewer - Full Size */}
      <div className="flex-1 relative">
        <D3HybridViewer
          nodes={graphData.nodes}
          links={graphData.links}
          onNodeClick={handleNodeClick}
          orientation={orientation}
          expandedIds={expandedIds}
          onToggleExpand={handleToggleExpand}
          powerVisMode={powerVisMode}
          selectedNodeIds={selectedNodeIds}
          onBackgroundClick={handleBackgroundClick}
          visibleNodeIds={visibleNodeIds}
          highlightedPath={highlightedPath}
          sidebarWidth={isSidebarOpen ? 384 : 0}
          nodeClickPhase={nodeClickPhase}
          expandingNodeId={expandingNodeId}
        />
      </div>

      {/* Details Panel (Sidebar) - Absolute overlay */}
      <div className="absolute top-0 right-0 h-full z-10">
        <DetailsPanel
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          selectedNodes={selectedNodes}
          onClose={handleDetailsPanelClose}
          nodes={graphData.nodes}
          links={graphData.links}
          onToggleType={handleToggleType}
          onCollapseTypeAndBelow={handleCollapseTypeAndBelow}
          expandedIds={expandedIds}
          powerVisMode={powerVisMode}
          onPowerSelect={handlePowerSelect}
          onPropertySelect={handlePropertySelect}
          onLegendClick={handleLegendClick}
          visibleNodeIds={visibleNodeIds}
          onMultiNodeSelect={handleMultiNodeSelect}
          orientation={orientation}
          onOrientationChange={setOrientation}
          onPowerVisChange={setPowerVisMode}
          onExpandAuxiliary={handleExpandAuxiliary}
          onCollapseAuxiliary={handleCollapseAuxiliary}
          shouldSwitchToDetails={shouldSwitchToDetails}
          onNodeSelect={handleSearchNodeSelect}
        />
      </div>
    </div>
  );
};

export default NetworkViewerTab;
